import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Table,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Tag,
  Statistic,
  Tooltip,
  Empty,
  Spin,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const BillingLogsManager = () => {
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalLogs, setTotalLogs] = useState(0);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const [filters, setFilters] = useState({
    eventType: "all",
    status: "all",
  });

  const eventTypes = [
    { label: "All Events", value: "all" },
    { label: "Checkout Created", value: "checkout_created" },
    { label: "Subscription Started", value: "subscription_started" },
    { label: "Subscription Created", value: "subscription_created" },
    { label: "Subscription Renewed", value: "subscription_renewed" },
    { label: "Subscription Canceled", value: "canceled" },
    { label: "Subscription Canceled (Legacy)", value: "subscription_canceled" },
    { label: "Trial Started", value: "trial_started" },
    { label: "Trial Ended", value: "trial_ended" },
    { label: "Payment Failed", value: "payment_failed" },
    { label: "Payment Success", value: "payment_success" },
    { label: "Payment Succeeded", value: "payment_succeeded" },
    { label: "Refund Issued", value: "refund_issued" },
    { label: "Plan Changed", value: "plan_changed" },
    { label: "Card Updated", value: "card_updated" },
  ];

  const statusOptions = [
    { label: "All Status", value: "all" },
    { label: "Success", value: "success" },
    { label: "Failed", value: "failed" },
    { label: "Pending", value: "pending" },
  ];

  useEffect(() => {
    fetchLogs();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.eventType, filters.status]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", pagination.current);
      queryParams.append("limit", pagination.pageSize);
      if (filters.eventType !== "all") queryParams.append("eventType", filters.eventType);
      if (filters.status !== "all") queryParams.append("status", filters.status);

      const url = `${apiPath.adminBillingLogs}?${queryParams.toString()}`;
      await request({
        url,
        method: "GET",
        onSuccess: (data) => {
          setLogs(data?.logs || []);
          setTotalLogs(data?.pagination?.total || 0);
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch billing logs";
          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      await request({
        url: apiPath.adminBillingStats,
        method: "GET",
        onSuccess: (data) => {

            console.log(data,"data stats here")
          setStats(data?.stats);
        },
        onError: (err) => {
          console.error("Failed to fetch billing stats", err);
        },
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const getEventTypeLabel = (eventType) => {
    const found = eventTypes.find((e) => e.value === eventType);
    return found ? found.label : eventType;
  };

  const getEventTypeColor = (eventType) => {
    if (eventType.includes("success") || eventType.includes("succeeded") || eventType.includes("created") || eventType.includes("started") || eventType.includes("renewed")) {
      return "green";
    }
    if (eventType.includes("failed") || eventType.includes("canceled")) {
      return "red";
    }
    if (eventType.includes("trial")) {
      return "blue";
    }
    return "default";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "green";
      case "failed":
        return "red";
      case "pending":
        return "orange";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("YYYY-MM-DD HH:mm:ss"),
      width: 150,
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      render: (eventType) => (
        <Tag color={getEventTypeColor(eventType)}>
          {getEventTypeLabel(eventType)}
        </Tag>
      ),
      width: 140,
    },
    {
      title: "User",
      dataIndex: "userId",
      key: "userId",
      render: (userId) => (
        <Tooltip title={userId?.email}>
          <span>{userId?.name || "—"}</span>
        </Tooltip>
      ),
      width: 120,
    },
    {
      title: "Plan",
      dataIndex: "planName",
      key: "planName",
      render: (plan) => plan || "—",
      width: 80,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => {
        if (!amount) return "—";
        return `£${(amount / 100).toFixed(2)} ${record.currency?.toUpperCase()}`;
      },
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
      width: 80,
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: 200,
      render: (msg) => (
        <Tooltip title={msg}>
          <span style={{ fontSize: "12px", color: "#999" }}>
            {msg ? msg.substring(0, 30) + "..." : "—"}
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Card
        bordered={false}
        className="criclebox"
        title="Billing Statistics"
        loading={statsLoading}
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="Trial Users"
              value={stats?.trialUsers || 0}
              className="trial-stat"
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="Active Paid Users"
              value={stats?.activePaidUsers || 0}
              className="paid-stat"
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="Canceled Users"
              value={stats?.canceledUsers || 0}
              className="cancel-stat"
            />
          </Col>
          {/* <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="MRR"
              value={stats?.mrr ? `£${parseFloat(stats.mrr).toFixed(2)}` : "£0"}
              valueStyle={{ color: "#722ed1" }}
            />
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="Conversion %"
              value={`${parseFloat(stats?.conversionPercent || 0).toFixed(2)}%`}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Col> */}
          <Col xs={12} sm={8} lg={4}>
            <Statistic
              title="Failed Payments"
              value={stats?.failedPayments || 0}
                className="failed-stat"
            />
          </Col>
        </Row>
      </Card>

      <Card
        bordered={false}
        className="criclebox"
        title="Billing Logs"
        extra={
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchLogs();
              fetchStats();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <Select
            style={{ width: 200 }}
            value={filters.eventType}
            onChange={(value) => {
              setFilters({ ...filters, eventType: value });
              setPagination({ current: 1, pageSize: 20 });
            }}
            options={eventTypes}
            placeholder="Filter by event type"
          />
          <Select
            style={{ width: 150 }}
            value={filters.status}
            onChange={(value) => {
              setFilters({ ...filters, status: value });
              setPagination({ current: 1, pageSize: 20 });
            }}
            options={statusOptions}
            placeholder="Filter by status"
          />
        </div>

        {logs.length === 0 && !loading ? (
          <Empty description="No billing logs found" />
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            loading={loading}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalLogs,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
            }}
            scroll={{ x: 1200 }}
          />
        )}
      </Card>
    </div>
  );
};

export default BillingLogsManager;
